import React, { useState, useEffect, useContext } from 'react';
import styles from './SingleResultsCard.module.css';
import { FaSpinner, FaCopy, FaCheck } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Editor, EditorState, ContentState } from 'draft-js';
import AuthContext from '../../context/AuthContext';
import AmazonValidationModal from '../UI/AmazonValidationModal';
import AmazonProcessingTimeModal from '../UI/AmazonProcessingTimeModal';
import AmazonImageSelectionModal from '../UI/AmazonImageSelectionModal';
import { highlightBannedWords, containsBannedWords, isTitleTooLong, isItemHighlightsTooLong, usesLegacyTitleFormat, LEGACY_FORMAT_MESSAGE, TITLE_MAX_LENGTH, ITEM_HIGHLIGHTS_MAX_LENGTH } from '../../utils/amazonValidation';
import { useNotification } from '../../context/NotificationContext';
import useSingleFlight from '../../hooks/useSingleFlight';
import DownloadCircleIcon from '../UI/DownloadCircleIcon';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const capitalizeKeyword = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Shows the keywords the model injected into a section. Click the count pill to
// expand/collapse the full keyword list as chips below the char-count row.
const KeywordCount = ({ keywords }) => {
    const [expanded, setExpanded] = useState(false);

    if (!Array.isArray(keywords) || keywords.length === 0) return null;

    const count = keywords.length;
    const label = `${count} ${count === 1 ? 'Keyword' : 'Keywords'}`;

    return (
        <>
            <span
                className={`${styles.charCount} ${styles.keywordCount}`}
                onClick={() => setExpanded(e => !e)}
            >
                {label} {expanded ? '▴' : '▾'}
            </span>
            {expanded && (
                <span className={styles.keywordChipRow}>
                    {keywords.map((k, i) => (
                        <span key={i} className={styles.keywordChip}>{capitalizeKeyword(k)}</span>
                    ))}
                </span>
            )}
        </>
    );
};

// Fetch a URL as a Blob, retrying transient failures (S3 throttling / network blips).
// Permanent failures (4xx other than 429) are not retried.
async function fetchBlob(url, retries = 3) {
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await fetch(url, { cache: 'no-store' });
            if (response.ok) {
                return await response.blob();
            }
            // Don't waste retries on permanent client errors (e.g. 403/404); only retry throttling/server errors
            if (response.status !== 429 && response.status < 500) {
                return null;
            }
        } catch (error) {
            // Network error / transient failure -> fall through to retry
        }
        if (attempt < retries) {
            await sleep(400 * (attempt + 1)); // linear backoff
        }
    }
    return null; // Indicate failure after exhausting retries
}

// Run async tasks over items with bounded concurrency, so we don't fire dozens of
// parallel fetches at once (which triggers S3 throttling and browser connection limits).
async function mapWithConcurrency(items, limit, task) {
    const results = new Array(items.length);
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (cursor < items.length) {
            const index = cursor++;
            results[index] = await task(items[index], index);
        }
    });
    await Promise.all(runners);
    return results;
}

// Child Details View Component
// Helper function to extract image URL from various formats
const extractImageUrl = (img) => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    return img.s3_url || img.url || null;
};

// Convert local dev URLs to relative paths for backend processing; external URLs pass through.
const toBackendImageUrl = (url) => {
    if (typeof url === 'string' && url.startsWith('http://localhost:5000/static/')) {
        return url.replace('http://localhost:5000', '');
    }
    return url;
};

// Helper function to get child infographics from either structure
const getChildInfographics = (child, childIndex, childInfographics) => {
    // Prioritize direct structure (child_output.children)
    if (child.aplus_templates || child.bplus_templates || child.application_templates) {
        return {
            aplus: child.aplus_templates || [],
            bplus: child.bplus_templates || [],
            application: child.application_templates || []
        };
    }
    // Fallback to old structure - use child_index if available, otherwise use childIndex + 1
    const actualChildIndex = child.child_index !== undefined ? child.child_index : (childIndex + 1);
    const childKey = `child${actualChildIndex}`;
    const infographicData = childInfographics.find(ci => ci.child_key === childKey) || {};
    return {
        aplus: infographicData.infographics?.aplus_templates || [],
        bplus: infographicData.infographics?.bplus_templates || [],
        application: infographicData.infographics?.application_templates || []
    };
};

// Helper function to extract child images from B+ templates (shared logic)
const extractChildBplusImages = (bplus) => {
    const singleImageTemplate = bplus[0]?.template_id === 'single_image' ? bplus[0] : null;
    const childSingleImage = extractImageUrl(singleImageTemplate?.images?.[0]);
    const childBplusImageUrls = (singleImageTemplate ? bplus.slice(1) : bplus)
        .flatMap(t => (t.images || []).map(extractImageUrl))
        .filter(Boolean);
    const uniqueChildBplusImageUrls = [...new Set(childBplusImageUrls)];
    return { childSingleImage, childBplusImageUrls: uniqueChildBplusImageUrls };
};

const ChildDetailsView = ({ childData, resultData, childIndex, onClose, authToken }) => {
    const [selectedImage, setSelectedImage] = useState('');
    const [selectedAplusImage, setSelectedAplusImage] = useState('');

    const childInfographics = resultData?.child_infographics || [];
    const { aplus, bplus } = getChildInfographics(childData, childIndex, childInfographics);
    
    // Extract images using shared helper
    const { childSingleImage, childBplusImageUrls } = extractChildBplusImages(bplus);
    
    const childAplusImages = aplus.flatMap(aplusTemplate => 
        (aplusTemplate.images || []).map((img, idx) => ({ 
            url: extractImageUrl(img), 
            slide_number: idx + 1 
        }))
    ).filter(img => img.url);

    const allChildImages = [childSingleImage, ...childBplusImageUrls, ...childAplusImages.map(img => img.url)].filter(Boolean);

    return (
        <div className={styles.childDetailsContainer}>
            {/* Child Title and Basic Info */}
            <div className={styles.childInfoSection}>
                <h3>{childData.title}</h3>
                <div className={styles.childMeta}>
                    <span><strong>SKU:</strong> {childData.model_number}</span>
                    <span><strong>Variation:</strong> {childData.variation_details || 'N/A'}</span>
                </div>
            </div>

            {/* Child Description */}
            {childData.description && (
                <div className={styles.childDescription}>
                    <h4>Description</h4>
                    <p>{childData.description}</p>
                </div>
            )}

            {/* Child Bullets */}
            {childData.bullets && childData.bullets.length > 0 && (
                <div className={styles.childBullets}>
                    <h4>About this item</h4>
                    <ul>
                        {childData.bullets.map((bullet, idx) => (
                            <li key={idx}>{bullet}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Application Templates (B+ Infographics) */}
            {childBplusImageUrls.length > 0 && (
                <div className={styles.childImagesSection}>
                    <h4>Application Templates / B+ Infographics ({childBplusImageUrls.length})</h4>

                    {/* Main Image Display */}
                    {childBplusImageUrls.length > 0 && (
                        <div className={styles.childMainImageContainer}>
                            <img
                                src={selectedImage || childBplusImageUrls[0]}
                                alt="Application Template"
                                className={styles.childMainImage}
                                onError={(e) => {
                                    e.target.src = '';
                                    e.target.alt = 'Image failed to load';
                                }}
                            />
                        </div>
                    )}

                    {/* Thumbnail Gallery */}
                    {childBplusImageUrls.length > 1 && (
                        <div className={styles.childThumbnailContainer}>
                            {childBplusImageUrls.map((url, index) => (
                                <img
                                    key={index}
                                    src={url}
                                    alt={`Application Template ${index + 1}`}
                                    className={`${styles.childThumbnail} ${selectedImage === url ? styles.thumbnailActive : ''}`}
                                    onClick={() => setSelectedImage(url)}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* A+ Templates Section */}
            {childAplusImages.length > 0 && (
                <div className={styles.childAplusSection}>
                    <h4>A+ Content ({childAplusImages.length} slides)</h4>
                    <div className={styles.childAplusContainer}>
                        {childAplusImages.length > 0 && (
                            <div className={styles.childAplusMain}>
                                <img
                                    src={selectedAplusImage || childAplusImages[0].url}
                                    alt="A+ Template"
                                    className={styles.childAplusImage}
                                    onError={(e) => {
                                        e.target.src = '';
                                        e.target.alt = 'A+ template failed to load';
                                    }}
                                />
                            </div>
                        )}

                        {childAplusImages.length > 1 && (
                            <div className={styles.childAplusThumbnails}>
                                {childAplusImages.map((img, index) => (
                                    <img
                                        key={index}
                                        src={img.url}
                                        alt={`A+ Slide ${img.slide_number || index + 1}`}
                                        className={`${styles.childAplusThumbnail} ${selectedAplusImage === img.url ? styles.aplusThumbnailActive : ''}`}
                                        onClick={() => setSelectedAplusImage(img.url)}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Child Single Image Section */}
            {childSingleImage && (
                <div className={styles.childImagesSection}>
                    <h4>Main Product Image</h4>
                    <div className={styles.childMainImageContainer}>
                        <img
                            src={childSingleImage}
                            alt="Child Product Main Image"
                            className={styles.childMainImage}
                            onError={(e) => {
                                e.target.src = '';
                                e.target.alt = 'Image failed to load';
                            }}
                        />
                    </div>
                </div>
            )}

            {/* B+ Templates Section */}
            {childBplusImageUrls.length > 0 && (
                <div className={styles.childBplusSection}>
                    <h4>B+ Templates ({childBplusImageUrls.length} images)</h4>
                    <div className={styles.childBplusContainer}>
                        {childBplusImageUrls.map((url, index) => (
                            <img
                                key={index}
                                src={url}
                                alt={`B+ Template ${index + 1}`}
                                className={styles.childBplusImage}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Show message if no infographics available */}
            {childBplusImageUrls.length === 0 && childAplusImages.length === 0 && (
                <div className={styles.childImagesSection}>
                    <h4>Infographics</h4>
                    <p style={{ color: '#666', fontStyle: 'italic', padding: '20px' }}>
                        No infographics generated for this variant yet. Infographics will be generated automatically when you create child variants with infographic generation enabled.
                    </p>
                </div>
            )}
        </div>
    );
};

const API_URL = process.env.REACT_APP_API_URL;
// API to save updated content to backend
async function saveUpdatedContentToBackend(contentData, token) {
    try {
        if (!token) {
            throw new Error('Authentication token not found. Please log in again.');
        }
        
        const response = await fetch(`${API_URL}/auth/listings/update-content`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`  // Add the Bearer token here
            },
            body: JSON.stringify(contentData),
        });

        if (!response.ok) {
            let errorMessage = `Failed to save content: ${response.status} ${response.statusText}`;
            try {
                const errorResponse = await response.json();
                if (errorResponse.error) {
                    errorMessage += ` - ${errorResponse.error}`;
                }
            } catch (e) {
                try {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage += ` - ${errorText}`;
                    }
                } catch (err) {
                    // fallback to original error message
                }
            }
            throw new Error(errorMessage);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'Unknown error occurred while saving content');
        }

        return data;
    } catch (error) {
        throw error;
    }
}

const SingleResultsCard = ({ resultData, onClose, isInline = false, onContentUpdated, authToken, isChildView = false, hideResultsHeader = false, hideProductImages = false, hideEditMoreOverlay = false, hideDownloadButton = false }) => {
    // --- Auth Context ---
    const authCtx = useContext(AuthContext);
    const isAmazonLinked = authCtx.user?.amazon_status === true;

    // --- Data Extraction ---
    const productInput = resultData?.product || {};
    const claudeOutput = resultData?.claude_output || {};

    // Get child products from either source, preferring non-empty arrays
    // Also check child_output.children structure
    let childProducts = resultData?.child_products?.length ? resultData.child_products :
                        claudeOutput?.child_products?.length ? claudeOutput.child_products : [];
    
    // If no child products found, check child_output.children structure
    if (!childProducts.length && claudeOutput?.child_output?.children) {
      const childrenObj = claudeOutput.child_output.children;
      childProducts = Object.keys(childrenObj).map(key => childrenObj[key]);
    }
    const childInfographics = resultData?.child_infographics || []; // Extract child infographics
    const listing_id = resultData?.inserted_id || null // Get the document ID
    const child_sku = resultData?.child_sku; // For child views, identifies which child to update (keyed by SKU)
    // Read aplus from claude_output.aplus_generated_images (flat URL list) or legacy aplus_templates
    const aplusTemplates = (claudeOutput?.aplus_generated_images || []).length > 0
        ? claudeOutput.aplus_generated_images.map((url, i) => ({ image_url: { url, page: `page_${i + 1}` } }))
        : (resultData?.aplus_templates || []);
    const bplusTemplates = resultData?.bplus_templates || []; // Extract B+ templates
    const editorFolderId = resultData?.editor_folder_id || null
    const listing_mode = resultData?.listing_mode;
    const [selectedAplusImage, setSelectedAplusImage] = useState('');
    const [selectedChild, setSelectedChild] = useState(null); // For showing child details in nested SingleResultsCard

    const hasAplusTemplates = aplusTemplates && aplusTemplates.length > 0;
    const hasBplusTemplates = bplusTemplates && bplusTemplates.length > 0;

    // Function to create child-specific result data for SingleResultsCard
    const createChildResultData = (child, childIndex) => {
        const { aplus, bplus } = getChildInfographics(child, childIndex, childInfographics);

        // Extract images using shared helper
        const { childSingleImage, childBplusImageUrls } = extractChildBplusImages(bplus);

        // Extract A+ images
        const childAplusImageUrls = aplus.flatMap(aplusTemplate => 
            (aplusTemplate.images || []).map(img => extractImageUrl(img))
        ).filter(Boolean);

        // Create child-specific data
        // IMPORTANT: Explicitly set bplus_templates to child's B+ images only (never use parent's)
        // IMPORTANT: Use child's editor_folder_id for separate editor projects (fall back to parent's if not available)
        const childResultData = {
            ...resultData,
            // Pass child_sku so save knows which child to update (keyed by SKU in database)
            child_sku: child.model_number || child.sku,
            // Override editor_folder_id with child's own folder (for separate editor projects)
            editor_folder_id: child.editor_folder_id || resultData.editor_folder_id,
            product: {
                ...resultData.product,
                model_number: child.model_number || child.sku,
                variation_details: child.variation_details
            },
            claude_output: {
                ...resultData.claude_output,
                title: child.title,
                item_highlights: child.item_highlights ?? resultData.claude_output.item_highlights,
                description: child.description || resultData.claude_output.description,
                bullets: child.bullets || resultData.claude_output.bullets,
                // child uses its own B+ templates, not parent's infographic_urls
                infographic_urls: [],
                aplus_generated_images: [],
                generated_images: childSingleImage ? [childSingleImage] : []
            },
            aplus_templates: aplus.flatMap(aplusTemplate =>
                (aplusTemplate.images || []).map((img, idx) => ({
                    image_url: { url: extractImageUrl(img) },
                    page: idx + 1
                }))
            ).filter(t => t.image_url.url),
            // CRITICAL: Always use child's B+ templates (includes parent application templates), even if empty (never fall back to parent's)
            bplus_templates: childBplusImageUrls,
            child_infographics: resultData.child_infographics || []
        };

        return childResultData;
    };

    // Function to open child details in nested SingleResultsCard
    const openChildDetails = (child, childIndex) => {
        const childResultData = createChildResultData(child, childIndex);
        setSelectedChild({ child, childResultData, childIndex });
    };

    // Function to close child details and return to parent view
    const closeChildDetails = () => {
        setSelectedChild(null);
    };

    const title = claudeOutput.title || "Title Not Generated";
    const itemHighlights = claudeOutput.item_highlights || "";
    const description = claudeOutput.description || "";
    const bullets = claudeOutput.bullets || [];
    // Keyword lists refreshed by a regeneration, so the chips match the new copy instead
    // of describing the text it replaced. Null until something regenerates.
    const [editedKeywords, setEditedKeywords] = useState(null);

    const keywordsUsed = claudeOutput.keywords_used || {};
    // A regeneration replaces the section's keywords in place; fall back to the stored map.
    const activeKeywords = editedKeywords || keywordsUsed;
    const titleKeywords = Array.isArray(activeKeywords.title) ? activeKeywords.title : [];
    const itemHighlightsKeywords = Array.isArray(activeKeywords.item_highlights) ? activeKeywords.item_highlights : [];
    const bulletKeywords = Array.isArray(activeKeywords.bullets) ? activeKeywords.bullets : [];
    const descriptionKeywords = Array.isArray(activeKeywords.description) ? activeKeywords.description : [];
    const callouts = claudeOutput.key_feature_callouts || [];
    const infographicUrls = claudeOutput.infographic_urls || [];
    const backendError = claudeOutput.error;
    const infographicErrors = claudeOutput.infographic_errors || [];
    const singleImage = claudeOutput.generated_images?.[0]; // First image is always single image
    const infographicUrlsFromGenerated = claudeOutput.generated_images?.slice(1) || [];
    const generatedImages = claudeOutput.generated_images || [];
    const legacyInfographicUrls = claudeOutput.infographic_urls || [];
    // Only use original images from Rainforest in regenerate flow (when showResultsOnly is true)
    const originalImages = resultData?.originalImages || [];
    const isRegenerateFlow = resultData?.showResultsOnly === true;
    const baseImages = (isRegenerateFlow && originalImages.length > 0) ? originalImages : (generatedImages.length > 0 ? generatedImages : legacyInfographicUrls);
    // Combine base images with B+ templates (B+ templates are array of URLs/strings)
    // For child views, baseImages already includes single image, so we only add B+ templates
    // Deduplicate to avoid showing the same image twice
    const bplusImageUrls = hasBplusTemplates ? bplusTemplates.map(template => 
        typeof template === 'string' ? template : (template?.url || template?.image_url?.url || template)
    ).filter(Boolean) : [];
    
    // Deduplicate: remove any B+ image URLs that are already in baseImages
    const uniqueBplusImageUrls = bplusImageUrls.filter(url => !baseImages.includes(url));
    const allImages = [...baseImages, ...uniqueBplusImageUrls];
    const parsing_warning = claudeOutput.parsing_warning;

    // --- State for UI ---
    const [selectedInfographicUrl, setSelectedInfographicUrl] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('content');
    const [copiedStates, setCopiedStates] = useState({});
    const [saveError, setSaveError] = useState('');
    
    // --- Single Edit Mode State ---
    const [isEditMode, setIsEditMode] = useState(false);
    const [hasContentChanged, setHasContentChanged] = useState(false);

    const [showTitleLengthModal, setShowTitleLengthModal] = useState(false);
    const [validationError, setValidationError] = useState({ type: '', message: '' });
    const [showProcessingTimeModal, setShowProcessingTimeModal] = useState(false);

    // --- Amazon image selection state ---
    const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
    const [existingAmazonImages, setExistingAmazonImages] = useState([]);
    const [existingAplusImages, setExistingAplusImages] = useState([]);
    const [isFetchingExistingImages, setIsFetchingExistingImages] = useState(false);
    // Images the user chose in the selection modal, carried into the push call
    const [selectedGalleryUrls, setSelectedGalleryUrls] = useState([]);
    const [selectedAplusUrls, setSelectedAplusUrls] = useState([]);

    // --- Regenerate-section (redo) state ---
    const [regen, setRegen] = useState({ open: false, section: null, instructions: '', loading: false, error: '' });

    // --- Legacy title-format conversion state ---
    const [titleFormat, setTitleFormat] = useState({ loading: false, error: '' });

    // --- Amazon Push Loading State ---
    const [isPushingToAmazon, setIsPushingToAmazon] = useState(false);
    const [amazonPushSuccess, setAmazonPushSuccess] = useState(false);
    
    // Global notification system
    const { showAmazonPushResult } = useNotification();

    // --- Editor States ---
    const [titleEditorState, setTitleEditorState] = useState(() => {
        return EditorState.createWithContent(ContentState.createFromText(title));
    });
    
    const [bulletEditorStates, setBulletEditorStates] = useState(() => {
        const bulletsArray = Array.isArray(bullets) ? bullets : [];
        return bulletsArray.map(bullet => 
            EditorState.createWithContent(ContentState.createFromText(bullet))
        );
    });
    
    const [descriptionEditorState, setDescriptionEditorState] = useState(() => {
        return EditorState.createWithContent(ContentState.createFromText(description));
    });

    const [itemHighlightsEditorState, setItemHighlightsEditorState] = useState(() => {
        return EditorState.createWithContent(ContentState.createFromText(itemHighlights));
    });

    // --- Edited Content State ---
    const [editedContent, setEditedContent] = useState({
        title: title,
        item_highlights: itemHighlights,
        bullets: bullets,
        description: description
    });

    // Monitor for changes in the editors
    useEffect(() => {
        const currentTitle = titleEditorState.getCurrentContent().getPlainText();
        const currentBullets = bulletEditorStates.map(state => 
            state.getCurrentContent().getPlainText().trim()
        ).filter(item => item !== '');
        const currentDescription = descriptionEditorState.getCurrentContent().getPlainText();
        const currentItemHighlights = itemHighlightsEditorState.getCurrentContent().getPlainText();

        const hasChanged =
            currentTitle !== title ||
            currentItemHighlights !== itemHighlights ||
            JSON.stringify(currentBullets) !== JSON.stringify(bullets) ||
            currentDescription !== description;

        setHasContentChanged(hasChanged);
    }, [titleEditorState, bulletEditorStates, descriptionEditorState, itemHighlightsEditorState, title, itemHighlights, bullets, description]);

    // --- Effects ---
    // Update editor states when the actual content values change (not on every resultData reference change)
    const bulletsKey = JSON.stringify(bullets);
    useEffect(() => {
        // Don't reset editor states while the user is actively editing
        if (isEditMode) return;

        setTitleEditorState(EditorState.createWithContent(ContentState.createFromText(title)));
        const bulletsArray = Array.isArray(bullets) ? bullets : [];
        setBulletEditorStates(bulletsArray.map(bullet =>
            EditorState.createWithContent(ContentState.createFromText(bullet))
        ));
        setDescriptionEditorState(EditorState.createWithContent(ContentState.createFromText(description)));
        setItemHighlightsEditorState(EditorState.createWithContent(ContentState.createFromText(itemHighlights)));

        setEditedContent({
            title,
            item_highlights: itemHighlights,
            bullets,
            description
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, itemHighlights, description, bulletsKey]);

    useEffect(() => {
        if (allImages.length > 0) {
            // Only set if not already set or if current selection is not in allImages
            if (!selectedInfographicUrl || !allImages.includes(selectedInfographicUrl)) {
                setSelectedInfographicUrl(allImages[0]);
            }
        } else if (selectedInfographicUrl) {
            setSelectedInfographicUrl('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allImages]);  


    useEffect(() => {
        if (!isInline) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isInline]);

    useEffect(() => {
    if (hasAplusTemplates && aplusTemplates.length > 0 && aplusTemplates[0].image_url?.url) {
        setSelectedAplusImage(aplusTemplates[0].image_url.url);
    }
    }, [hasAplusTemplates, aplusTemplates]);


    // Reset active tab to 'content' when viewing a child product
    useEffect(() => {
        if (isChildView && activeTab === 'variations') {
            setActiveTab('content');
        }
    }, [isChildView]);


    // --- Handlers ---
    const handleThumbnailSelect = (url) => {
        setSelectedInfographicUrl(url);
    };

    const handleOverlayClick = (e) => {
        if (!isInline && e.target === e.currentTarget) {
            onClose();
        }
    };

    const toggleReadMore = () => {
        setIsExpanded(!isExpanded);
    };

    const copyToClipboard = (text, fieldName) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedStates(prev => ({ ...prev, [fieldName]: true }));
                setTimeout(() => {
                    setCopiedStates(prev => ({ ...prev, [fieldName]: false }));
                }, 1500);
            })
            .catch(err => {
            });
    };

    const copyAllCallouts = () => {
        const allCalloutsText = callouts.join('\n');
        copyToClipboard(allCalloutsText, 'All Callouts');
    };

    // --- Amazon Push Handler ---
    const handlePushToAmazon = async () => {
        try {
            // Reset success state
            setAmazonPushSuccess(false);

            // Block the push while the listing is still on the old title format —
            // pushing now would send a title Amazon no longer displays in full and no
            // Item Highlights at all.
            if (isLegacyFormat) {
                setValidationError({
                    type: 'legacy_format',
                    message: `${LEGACY_FORMAT_MESSAGE} Use "Update to new format" above to convert it before pushing to Amazon.`,
                });
                setShowTitleLengthModal(true);
                return;
            }

            // Check for validation issues
            const hasBannedWords = containsBannedWords(editedContent.title);
            const isTooLong = isTitleTooLong(editedContent.title);
            
            if (hasBannedWords || isTooLong) {
                let errorType = '';
                let errorMessage = '';
                
                if (hasBannedWords && isTooLong) {
                    errorType = 'both';
                    errorMessage = `The title contains banned keywords (highlighted in red) AND exceeds ${TITLE_MAX_LENGTH} characters. Please use the 'Edit Content' tool to remove banned words and shorten the title, then save changes to proceed listing on Amazon.`;
                } else if (hasBannedWords) {
                    errorType = 'banned_words';
                    errorMessage = 'The title contains banned keywords (highlighted in red). Please use the \'Edit Content\' tool to remove these words and save changes to proceed listing on Amazon.';
                } else if (isTooLong) {
                    errorType = 'title_length';
                    errorMessage = `The title is more than ${TITLE_MAX_LENGTH} characters. Please use the 'Edit Content' tool and save changes to proceed listing on Amazon.`;
                }
                
                setValidationError({
                    type: errorType,
                    message: errorMessage
                });
                setShowTitleLengthModal(true);
                return;
            }
            
            // Open the image selection modal and fetch existing Amazon images in the background
            setShowImageSelectionModal(true);
            fetchExistingAmazonImages();
        } catch (error) {
        }
    };

    // Fetch the listing's existing Amazon gallery images via the same catalogue lookup
    // used by the "fetch data from catalogue" flow. Failures degrade gracefully to
    // an empty list so the user can still push generated images.
    const fetchExistingAmazonImages = async () => {
        if (!productInput.asin) return;
        setIsFetchingExistingImages(true);
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

            const response = await fetch(`${API_URL}/amazon/catalogue/lookup`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    asin: productInput.asin,
                    amazon_domain: 'amazon.in',
                    listingMode: 'existing',
                }),
            });

            if (!response.ok) {
                setExistingAmazonImages([]);
                setExistingAplusImages([]);
                return;
            }

            const data = await response.json();
            const result = data?.result || {};
            const urls = [];
            if (result.main_image) urls.push(result.main_image);
            if (Array.isArray(result.all_images)) urls.push(...result.all_images);
            setExistingAmazonImages([...new Set(urls.filter(Boolean))]);
            // A+ entries can be plain URL strings or { link, alt } objects — normalize to URLs.
            const aplusUrls = (Array.isArray(result.aplus_images) ? result.aplus_images : [])
                .map(img => (typeof img === 'string' ? img : (img?.link || img?.url)))
                .filter(url => typeof url === 'string' && url.startsWith('http'));
            setExistingAplusImages([...new Set(aplusUrls)]);
        } catch (error) {
            setExistingAmazonImages([]);
            setExistingAplusImages([]);
        } finally {
            setIsFetchingExistingImages(false);
        }
    };

    // User confirmed their image selection -> carry it into the push and show the
    // processing-time confirmation modal.
    const handleImageSelectionConfirm = (galleryUrls, aplusUrls) => {
        setSelectedGalleryUrls(galleryUrls);
        setSelectedAplusUrls(aplusUrls);
        setShowImageSelectionModal(false);
        setShowProcessingTimeModal(true);
    };

    const pushToAmazon = async () => {
        try {
            setShowProcessingTimeModal(false);
            
            // Start loading
            setIsPushingToAmazon(true);

            // Prepare update data for Amazon
            const updateData = {
                asin: productInput.asin,
                updateListingData: {
                    title: editedContent.title || title,
                    description: editedContent.description || description,
                    bullets: editedContent.bullets || bullets,
                    item_highlights: editedContent.item_highlights ?? itemHighlights,
                    // Only push the images the user selected in the selection modal.
                    // Convert local dev URLs to relative paths for backend processing.
                    infographicUrls: selectedGalleryUrls.map(toBackendImageUrl),
                    aplusImageUrls: selectedAplusUrls.map(toBackendImageUrl)
                }
            };


            // Call the Amazon update endpoint
            const response = await fetch(`${API_URL}/oauth_amazon/spapi/update_amznProduct_listing`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                const result = await response.json();
                setAmazonPushSuccess(true);
                showAmazonPushResult(true, `Your listing for ASIN <span style="font-weight: bold; color: #1B2833;">${productInput.asin}</span> has been submitted successfully to Amazon!`);
            } else {
                const errorData = await response.json();
                showAmazonPushResult(false, `Failed to submit ASIN <span style="font-weight: bold; color: #1B2833;">${productInput.asin}</span> to Amazon.`);
            }
        } catch (error) {
            showAmazonPushResult(false, `Failed to submit ASIN <span style="font-weight: bold; color: #1B2833;">${productInput.asin}</span> to Amazon.`);
        } finally {
            // End loading
            setIsPushingToAmazon(false);
        }
    };

    // The confirmation modal's "Yes" button has no disabled state, so rage clicks
    // would PATCH the same ASIN on Amazon once per click.
    const handleConfirmProcessingTime = useSingleFlight(pushToAmazon);

    // --- Edit Mode Handlers ---
    const toggleEditMode = () => {
        if (isEditMode) {
            // Save all content
            saveAllContent();
        } else {
            // Enter edit mode
            setIsEditMode(true);
        }
    };

    const cancelEditMode = () => {
        // Restore editors to last saved content
        setTitleEditorState(EditorState.createWithContent(ContentState.createFromText(editedContent.title || '')));
        const bulletsArray = Array.isArray(editedContent.bullets) ? editedContent.bullets : [];
        setBulletEditorStates(bulletsArray.map(bullet =>
            EditorState.createWithContent(ContentState.createFromText(bullet))
        ));
        setDescriptionEditorState(EditorState.createWithContent(ContentState.createFromText(editedContent.description || '')));
        setItemHighlightsEditorState(EditorState.createWithContent(ContentState.createFromText(editedContent.item_highlights || '')));
        setSaveError('');
        setIsEditMode(false);
    };

    const saveAllContent = async () => {
        // Get content from all editors
        const updatedTitle = titleEditorState.getCurrentContent().getPlainText();
        const updatedItemHighlights = itemHighlightsEditorState.getCurrentContent().getPlainText();
        const updatedBullets = bulletEditorStates.map(state =>
            state.getCurrentContent().getPlainText().trim()
        ).filter(item => item !== '');
        const updatedDescription = descriptionEditorState.getCurrentContent().getPlainText();

        // Update the state with edited content
        setEditedContent({
            title: updatedTitle,
            item_highlights: updatedItemHighlights,
            bullets: updatedBullets,
            description: updatedDescription
        });

        // Prepare data for API call
        const updatedContent = {
            id: listing_id, // Add the document ID
            ...(child_sku && { child_sku }), // Include child_sku for child updates
            ...(productInput.model_number && { sku: productInput.model_number }), // Include SKU for bulk updates
            original: {
                title: title,
                item_highlights: itemHighlights,
                bullets: bullets,
                description: description
            },
            updated: {
                title: updatedTitle,
                item_highlights: updatedItemHighlights,
                bullets: updatedBullets,
                description: updatedDescription,
                // Carries any list a regeneration refreshed; identical to what is stored
                // otherwise, so an ordinary edit leaves the keywords untouched.
                keywords_used: activeKeywords
            }
        };

        setSaveError('');
        setIsSaving(true);

        try {
            // Send updated content to backend only if changes were detected
            if (hasContentChanged) {
                const result = await saveUpdatedContentToBackend(updatedContent, authToken);
                
                if (!result.success) {
                    throw new Error(result.error || 'Failed to save content');
                }
                
                // Notify parent component that content has been updated
                // Pass only the updated content, not the full structure
                if (onContentUpdated) {
                    onContentUpdated({
                        title: updatedTitle,
                        item_highlights: updatedItemHighlights,
                        bullets: updatedBullets,
                        description: updatedDescription,
                        _isChildUpdate: !!child_sku  // Flag to prevent parent state update
                    });
                }

                // For child views, close and return to parent after save
                if (child_sku && onClose) {
                    onClose();
                    return;
                }

            }
            setIsEditMode(false);
        } catch (error) {
            setSaveError('Failed to save content. Changes will be used for download only.');
        } finally {
            setIsSaving(false);
        }
    };

    const downloadAssets = async () => {
        if (isDownloading) return;
        setIsDownloading(true);

        try {
            // If in edit mode, save the content first
            if (isEditMode) {
                await saveAllContent();
            }
            
            // If content has changed but hasn't been saved yet (edit mode was exited without saving),
            // save it now before downloading
            if (hasContentChanged && !isEditMode) {
                const currentTitle = titleEditorState.getCurrentContent().getPlainText();
                const currentItemHighlights = itemHighlightsEditorState.getCurrentContent().getPlainText();
                const currentBullets = bulletEditorStates.map(state =>
                    state.getCurrentContent().getPlainText().trim()
                ).filter(item => item !== '');
                const currentDescription = descriptionEditorState.getCurrentContent().getPlainText();

                // Prepare data for API call
                const updatedContent = {
                    id: listing_id,
                    ...(child_sku && { child_sku }), // Include child_sku for child updates
                    ...(productInput.model_number && { sku: productInput.model_number }), // Include SKU for bulk updates
                    original: {
                        title: title,
                        item_highlights: itemHighlights,
                        bullets: bullets,
                        description: description
                    },
                    updated: {
                        title: currentTitle,
                        item_highlights: currentItemHighlights,
                        bullets: currentBullets,
                        description: currentDescription
                    }
                };
                
                try {
                    // Send updated content to backend
                    if (hasContentChanged) {
                        const result = await saveUpdatedContentToBackend(updatedContent, authToken);
                        
                        if (!result.success) {
                            throw new Error(result.error || 'Failed to save content');
                        }
                        
                        // Update edited content state
                        setEditedContent({
                            title: currentTitle,
                            item_highlights: currentItemHighlights,
                            bullets: currentBullets,
                            description: currentDescription
                        });

                        // Notify parent component that content has been updated
                        if (onContentUpdated) {
                            onContentUpdated({
                                title: currentTitle,
                                item_highlights: currentItemHighlights,
                                bullets: currentBullets,
                                description: currentDescription
                            });
                        }
                    }
                } catch (error) {
                    setSaveError('Failed to save content, but download will proceed with your changes.');
                }
            }

            const zip = new JSZip();
            const safeModelNumber = (productInput.model_number || 'product').replace(/[^a-z0-9_\-]/gi, '_');

            // 1. Prepare and Add Excel Data
            const excelDataRows = [];

            // Use edited content if available
            const finalTitle = editedContent.title || title;
            const finalItemHighlights = editedContent.item_highlights ?? itemHighlights;
            const finalBullets = editedContent.bullets || bullets;
            const finalDescription = editedContent.description || description;

            // Parent product row
            excelDataRows.push({
                'Type': productInput.is_parent ? 'Parent' : 'Standalone',
                'Brand Name': productInput.brand_name || '',
                'Category': productInput.category || '',
                'Sub Category': productInput.sub_category || '',
                'Model Number/SKU': productInput.model_number || '',
                'Parent SKU': productInput.is_parent ? (productInput.model_number || '') : (productInput.parent_sku || ''),
                'Title': finalTitle === "Title Not Generated" ? '' : finalTitle,
                'Item Highlights': finalItemHighlights || '',
                'Description': finalDescription,
                'Bullet 1': finalBullets[0] || '',
                'Bullet 2': finalBullets[1] || '',
                'Bullet 3': finalBullets[2] || '',
                'Bullet 4': finalBullets[3] || '',
                'Bullet 5': finalBullets[4] || '',
                'Callouts': callouts.join(' | '),
                'Variation Attributes': productInput.is_parent ? (productInput.attribute_sets?.map(attr => `${attr.name}: ${attr.values?.join(', ')}`).join(' | ') || '') : (productInput.variation_details || ''),
            });

            // Child product rows (if any)
            if (childProducts && childProducts.length > 0) {
                childProducts.forEach(child => {
                    excelDataRows.push({
                        'Type': 'Child',
                        'Brand Name': productInput.brand_name || '', // Inherited
                        'Category': productInput.category || '',   // Inherited
                        'Sub Category': productInput.sub_category || '', // Inherited
                        'Model Number/SKU': child.model_number || '',
                        'Parent SKU': child.parent_sku || productInput.model_number || '', // Parent's SKU
                        'Title': child.title || '',
                        'Item Highlights': child.item_highlights || '',
                        'Description': child.description || finalDescription, // Child might have own, or inherit
                        'Bullet 1': (child.bullets && child.bullets[0]) || finalBullets[0] || '',
                        'Bullet 2': (child.bullets && child.bullets[1]) || finalBullets[1] || '',
                        'Bullet 3': (child.bullets && child.bullets[2]) || finalBullets[2] || '',
                        'Bullet 4': (child.bullets && child.bullets[3]) || finalBullets[3] || '',
                        'Bullet 5': (child.bullets && child.bullets[4]) || finalBullets[4] || '',
                        'Callouts': '', // Callouts usually for parent/infographics
                        'Variation Attributes': child.variation_details || (Array.isArray(child.variation_attributes) ? child.variation_attributes.join(', ') : ''),
                    });
                });
            }
            
            const worksheet = XLSX.utils.json_to_sheet(excelDataRows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Listing Data");
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const excelBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            zip.file(`listing_${safeModelNumber}.xlsx`, excelBlob);

            // 2. Fetch and Add Infographic Images
            let imagesAddedCount = 0;
            let imageDownloadErrors = [];
            
            // Add regular infographic images
            if (allImages.length > 0) {
                const imagesFolder = zip.folder("images");
                await mapWithConcurrency(allImages, 5, async (url, index) => {
                    const blob = await fetchBlob(url);
                    if (blob) {
                        let filename = `infographic_${index + 1}.png`;
                        try {
                            const urlPath = new URL(url).pathname;
                            const parts = urlPath.split('/');
                            const lastPart = parts[parts.length - 1];
                            if (lastPart && lastPart.includes('.')) {
                                // Prefix with index so images sharing a basename across different
                                // URL paths don't collide and overwrite each other in the zip
                                filename = `${index + 1}_${lastPart.replace(/[^a-z0-9_\-\.]/gi, '_')}`;
                            }
                        } catch (e) { }
                        imagesFolder.file(filename, blob);
                        imagesAddedCount++;
                    } else {
                        imageDownloadErrors.push(`Failed to download image: ${url}`);
                    }
                });
            }
            
            // Add A+ Template Images
            if (hasAplusTemplates && aplusTemplates.length > 0) {
                const aplusFolder = zip.folder("aplus_templates");
                await mapWithConcurrency(aplusTemplates, 5, async (template, index) => {
                    const url = template.image_url.url;
                    const blob = await fetchBlob(url);
                    if (blob) {
                        let filename = `aplus_template_${index + 1}.png`;
                        try {
                            const urlPath = new URL(url).pathname;
                            const parts = urlPath.split('/');
                            const lastPart = parts[parts.length - 1];
                            if (lastPart && lastPart.includes('.')) {
                                // Prefix with index to avoid basename collisions overwriting entries
                                filename = `aplus_template_${index + 1}_${lastPart.replace(/[^a-z0-9_\-\.]/gi, '_')}`;
                            }
                        } catch (e) {
                            filename = `aplus_template_${template.image_url.page || index + 1}.png`;
                        }
                        aplusFolder.file(filename, blob);
                        imagesAddedCount++;
                    } else {
                        imageDownloadErrors.push(`Failed to download A+ template: ${url}`);
                    }
                });
            }

            // 2.5 Add Errors/Warnings to a text file
            let errorContent = "";
            if(backendError) errorContent += `Content Generation Error:\n${backendError}\n\n`;
            if(parsing_warning) errorContent += `Parsing Warning:\n${parsing_warning}\n\n`;
            if(infographicErrors.length > 0) errorContent += `Infographic Status:\n${infographicErrors.join('\n')}\n\n`;
            if(imageDownloadErrors.length > 0) errorContent += `Image Download Errors:\n${imageDownloadErrors.join('\n')}\n\n`;

            if (errorContent.trim()) {
                zip.file(`generation_report.txt`, errorContent.trim());
            }

            // 3. Generate Zip and Trigger Download
            if (imagesAddedCount > 0 || excelBlob || errorContent.trim()) {
                const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
                saveAs(zipBlob, `listing_${safeModelNumber}_assets.zip`);
            } 

        } catch (error) {
        } finally {
            setIsDownloading(false);
        }
    };

    // Repeated clicks would otherwise re-save content and rebuild the zip per click.
    const handleDownload = useSingleFlight(downloadAssets);

    // --- Render Logic ---
    const hasImages = allImages && allImages.length > 0;
    const hasCallouts = callouts && callouts.length > 0;
    const hasInfographicProcessingInfo = infographicUrls.length > 0 || infographicErrors.length > 0;
    const hasChildProducts = childProducts && childProducts.length > 0;

    const allErrorsAndWarnings = [
        ...(saveError ? [{ type: 'error', text: saveError }] : []),
        ...(backendError ? [{ type: 'error', text: `Content Generation Error: ${backendError}` }] : []),
        ...(parsing_warning ? [{ type: 'warning', text: `Parsing Warning: ${parsing_warning}` }] : []),
        ...(infographicErrors.length > 0 ? [{ type: 'info', text: 'Infographic Status:', items: infographicErrors }] : []),
    ];

    // Process Description for Read More
    const finalTitle = editedContent.title || title;
    const finalItemHighlights = editedContent.item_highlights ?? itemHighlights;
    const finalBullets = editedContent.bullets || bullets;
    const finalDescription = editedContent.description || description;

    // Listings without Item Highlights predate Amazon's 31-Jul-2026 format change, so
    // they keep the old presentation (no 75-char cap, no highlights field) until the
    // user converts them. Deriving this from the content means a successful conversion
    // flips the view on its own, with no extra state to keep in sync.
    const isLegacyFormat = usesLegacyTitleFormat(finalItemHighlights);


    // --- Regenerate a single section with user instructions ---
    // Toggle the inline box for a section (click the redo icon again to close).
    const openRegen = (section) => setRegen((r) => (
        r.open && r.section === section
            ? { ...r, open: false }
            : { open: true, section, instructions: '', loading: false, error: '' }
    ));
    const closeRegen = () => setRegen((r) => (r.loading ? r : { ...r, open: false }));

    // Convert a legacy listing to the modular title format, then persist it so the new
    // copy survives a reload. Uses /regenerate-section (free) rather than
    // /generate-title-highlights, which bills 2 credits per ASIN — migrating a listing
    // to Amazon's new format shouldn't cost the user anything.
    const convertTitleFormat = async () => {
        const token = authToken || authCtx.token;
        const originalTitle = finalTitle;

        const regenerateSection = async (section, instructions, currentValue, titleContext) => {
            const response = await fetch(`${API_URL}/single-gen/regenerate-section`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    section,
                    instructions,
                    product: productInput,
                    current: currentValue,
                    ...(titleContext && { title: titleContext }),
                }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || data.message || `Request failed: ${response.status}`);
            }
            return { content: data.content, keywords: Array.isArray(data.keywords) ? data.keywords : [] };
        };

        setTitleFormat({ loading: true, error: '' });
        try {
            // Title first: the highlights call needs it to avoid repeating its words.
            const titleResult = await regenerateSection(
                'title',
                'Rewrite this title for Amazon\'s shortened title format. Keep the brand, the '
                + 'product type and the highest-value search terms. Do not invent attributes that '
                + 'are not already in the title.',
                originalTitle,
            );
            const nextTitle = titleResult.content;
            if (!nextTitle) throw new Error('No title was returned. Please try again.');

            const highlightsResult = await regenerateSection(
                'item_highlights',
                'Write item highlights from the attributes in this original title that no longer '
                + `fit in the shortened one: "${originalTitle}". Do not repeat any word already `
                + 'present in the new title.',
                '',
                nextTitle,
            );
            const nextHighlights = highlightsResult.content;

            // Both sections were rewritten, so their old keyword lists no longer describe
            // them. Merge over the existing map so bullets/description keywords survive.
            const nextKeywords = {
                ...keywordsUsed,
                title: titleResult.keywords,
                item_highlights: highlightsResult.keywords,
            };

            const saved = await saveUpdatedContentToBackend({
                id: listing_id,
                ...(child_sku && { child_sku }),
                ...(productInput.model_number && { sku: productInput.model_number }),
                original: { title, item_highlights: itemHighlights, bullets, description },
                updated: {
                    title: nextTitle,
                    item_highlights: nextHighlights,
                    bullets: finalBullets,
                    description: finalDescription,
                    keywords_used: nextKeywords,
                },
            }, token);
            if (!saved.success) throw new Error(saved.error || 'Failed to save the updated title.');

            setEditedContent((prev) => ({ ...prev, title: nextTitle, item_highlights: nextHighlights }));
            setEditedKeywords(nextKeywords);
            setTitleFormat({ loading: false, error: '' });
            if (onContentUpdated) {
                onContentUpdated({
                    title: nextTitle,
                    item_highlights: nextHighlights,
                    bullets: finalBullets,
                    description: finalDescription,
                    keywords_used: nextKeywords,
                    _isChildUpdate: !!child_sku,
                });
            }
        } catch (error) {
            setTitleFormat({ loading: false, error: error.message || 'Could not update the title format.' });
        }
    };

    // Core regeneration for one section. Sends the current title alongside so
    // the backend can keep item_highlights free of title overlap.
    const runRegen = async (section, instructions) => {
        setRegen((r) => ({ ...r, section, loading: true, error: '' }));
        try {
            const current = section === 'title' ? finalTitle
                : section === 'item_highlights' ? finalItemHighlights
                    : section === 'bullets' ? finalBullets
                        : finalDescription;
            const response = await fetch(`${API_URL}/single-gen/regenerate-section`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken || authCtx.token}`,
                },
                body: JSON.stringify({ section, instructions, product: productInput, current, title: finalTitle }),
            });
            const data = await response.json();
            if (!response.ok || !data.success) throw new Error(data.error || 'Failed to regenerate.');

            // Next content for all fields — pass the FULL set to onContentUpdated
            // (some parents, e.g. bulk, replace rather than merge, so a partial
            // update would wipe the unchanged fields).
            let nextTitle = finalTitle;
            let nextItemHighlights = finalItemHighlights;
            let nextBullets = finalBullets;
            let nextDescription = finalDescription;
            if (section === 'title') {
                nextTitle = String(data.content);
                setEditedContent((p) => ({ ...p, title: nextTitle }));
                setTitleEditorState(EditorState.createWithContent(ContentState.createFromText(nextTitle)));
            } else if (section === 'item_highlights') {
                nextItemHighlights = String(data.content);
                setEditedContent((p) => ({ ...p, item_highlights: nextItemHighlights }));
                setItemHighlightsEditorState(EditorState.createWithContent(ContentState.createFromText(nextItemHighlights)));
            } else if (section === 'bullets') {
                nextBullets = Array.isArray(data.content) ? data.content.map(String) : [String(data.content)];
                setEditedContent((p) => ({ ...p, bullets: nextBullets }));
                setBulletEditorStates(nextBullets.map((x) => EditorState.createWithContent(ContentState.createFromText(x))));
            } else {
                nextDescription = String(data.content);
                setEditedContent((p) => ({ ...p, description: nextDescription }));
                setDescriptionEditorState(EditorState.createWithContent(ContentState.createFromText(nextDescription)));
            }
            // The rewritten section's old keywords describe text that no longer exists,
            // so swap just that entry and keep the other sections' lists intact.
            const nextKeywords = {
                ...activeKeywords,
                [section]: Array.isArray(data.keywords) ? data.keywords : [],
            };
            setEditedKeywords(nextKeywords);

            if (onContentUpdated) {
                onContentUpdated({
                    title: nextTitle,
                    item_highlights: nextItemHighlights,
                    bullets: nextBullets,
                    description: nextDescription,
                    keywords_used: nextKeywords,
                });
            }
            setRegen({ open: false, section: null, instructions: '', loading: false, error: '' });
        } catch (e) {
            setRegen((r) => ({ ...r, loading: false, error: e.message || 'Failed to regenerate.' }));
        }
    };

    const submitRegen = async () => {
        const section = regen.section;
        const instructions = (regen.instructions || '').trim();
        if (!instructions) { setRegen((r) => ({ ...r, error: 'Please enter instructions.' })); return; }
        await runRegen(section, instructions);
    };

    // Inline instructions box rendered below the given section when its redo is open.
    const renderRegenBox = (section) => {
        if (!regen.open || regen.section !== section) return null;
        return (
            <div className={styles.regenBox}>
                <textarea
                    className={styles.regenTextarea}
                    value={regen.instructions}
                    onChange={(e) => setRegen((r) => ({ ...r, instructions: e.target.value }))}
                    placeholder="Describe what to change… e.g. make it more premium, highlight the 5-year warranty, add the pack size"
                    rows={3}
                    autoFocus
                    disabled={regen.loading}
                />
                {regen.error && <div className={styles.regenError}>{regen.error}</div>}
                <div className={styles.regenActions}>
                    <button
                        type="button"
                        className={`${styles.regenBtn} ${styles.regenBtnSecondary}`}
                        onClick={closeRegen}
                        disabled={regen.loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`${styles.regenBtn} ${styles.regenBtnPrimary}`}
                        onClick={submitRegen}
                        disabled={regen.loading || !regen.instructions.trim()}
                    >
                        {regen.loading ? 'Regenerating…' : 'Regenerate'}
                    </button>
                </div>
            </div>
        );
    };

    // --- Banned Words Detection ---
    const hasBannedWords = containsBannedWords(finalTitle);
    
    const descriptionParagraphs = finalDescription.split('\n').filter(p => p.trim() !== '');
    const needsTruncation = descriptionParagraphs.length > 1;
    const displayedDescription = needsTruncation && !isExpanded
        ? descriptionParagraphs[0]
        : descriptionParagraphs.join('\n');

        // If a child is selected, show only the nested SingleResultsCard
        if (selectedChild) {
            return (
                <SingleResultsCard
                    resultData={selectedChild.childResultData}
                    onClose={closeChildDetails}
                    isInline={isInline}
                    onContentUpdated={onContentUpdated}
                    authToken={authToken}
                    hideProductImages={hideProductImages}
                    isChildView={true}
                />
            );
        }

        return (
            <>
            {isInline && !hideResultsHeader && (
                <div className={styles.resultsHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {onClose && (
                            <button 
                                className={styles.backButton}
                                onClick={onClose}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_35_7)">
                                    <path d="M19.0476 8.33333H4.55953L11.2143 1.67857L9.52381 0L0 9.52381L9.52381 19.0476L11.2024 17.369L4.55953 10.7143H19.0476V8.33333Z" fill="#505050"/>
                                    </g>
                                    <defs>
                                    <clipPath id="clip0_35_7">
                                    <rect width="20" height="20" fill="white"/>
                                    </clipPath>
                                    </defs>
                                </svg>
                            </button>
                        )}
                        <h1>{resultData?.product?.project_name || 'Generation Details'}</h1>
                    </div>
                    <div className={styles.resultsHeaderButtons}>
                        {isEditMode ? (
                            <>
                                <button
                                    className={styles.downloadContentButton}
                                    onClick={cancelEditMode}
                                    disabled={isSaving}
                                >
                                    <span className={styles.downloadContentButtonText}>Discard Changes</span>
                                </button>
                                <button
                                    className={styles.pushToAmazonButton}
                                    onClick={toggleEditMode}
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <>
                                            <FaSpinner className={styles.spinner} style={{ marginRight: '8px' }}/>
                                            <span className={styles.pushToAmazonButtonText}>Saving...</span>
                                        </>
                                    ) : (
                                        <span className={styles.pushToAmazonButtonText}>Save Changes</span>
                                    )}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className={styles.pushToAmazonButton}
                                    onClick={handlePushToAmazon}
                                    disabled={isDownloading || isPushingToAmazon || !isAmazonLinked || !productInput.asin || (!editedContent.title && !editedContent.description && !editedContent.bullets.length) || listing_mode === 'new'}
                                    title={!isAmazonLinked ? 'Amazon account not connected - please connect your Amazon account first' : !productInput.asin ? 'No ASIN found - fetch product via ASIN first' : (!editedContent.title && !editedContent.description && !editedContent.bullets.length) ? 'No content to push - generate or edit content first' : listing_mode === 'new' ? 'Push to Amazon is only available for existing listings' : null}
                                >
                                    {isPushingToAmazon ? (
                                        <>
                                            <FaSpinner className={styles.spinner} style={{ marginRight: '8px' }}/>
                                            <span className={styles.pushToAmazonButtonText}>Pushing to Amazon...</span>
                                        </>
                                    ) : amazonPushSuccess ? (
                                        <span className={styles.pushToAmazonButtonText}>Submitted successfully</span>
                                    ) : (
                                        <>
                                            <div className={styles.pushToAmazonIcon}>
                                                <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M7.00031 11.8175C7.41088 11.8175 7.7432 11.4847 7.7432 11.0746V2.52743L11.1773 5.96206C11.3224 6.10717 11.5126 6.17948 11.7028 6.17948C11.893 6.17948 12.0832 6.10717 12.2283 5.96206C12.5185 5.67184 12.5185 5.20184 12.2283 4.91161L7.52578 0.209123C7.24695 -0.0697078 6.75367 -0.0697078 6.47533 0.209123L1.77284 4.91161C1.48262 5.20184 1.48262 5.67184 1.77284 5.96206C2.06306 6.25228 2.53307 6.25228 2.82329 5.96206L6.25742 2.52743V11.0746C6.25742 11.4847 6.58974 11.8175 7.00031 11.8175Z" fill="white"/>
                                                <path d="M0.743073 9.42444C0.332502 9.42444 0.000183105 9.75725 0.000183105 10.1673V13.6109C0.000183105 14.0209 0.332502 14.3538 0.743073 14.3538H13.2573C13.6679 14.3538 14.0002 14.0209 14.0002 13.6109V10.1673C14.0002 9.75725 13.6679 9.42444 13.2573 9.42444C12.8467 9.42444 12.5144 9.75725 12.5144 10.1673V12.868H1.48596V10.1673C1.48596 9.75725 1.15364 9.42444 0.743073 9.42444Z" fill="white"/>
                                                </svg>
                                            </div>
                                            <span className={styles.pushToAmazonButtonText}>Push to Amazon</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    className={styles.downloadContentButton}
                                    onClick={handleDownload}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? (
                                        <>
                                            <FaSpinner className={styles.spinner} style={{ marginRight: '8px' }}/>
                                            <span className={styles.downloadContentButtonText}>Downloading...</span>
                                        </>
                                    ) : (
                                        <span className={styles.downloadContentButtonText}>Download Content</span>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
            <div className={styles.inlineContainer} >
                {/* Back button for child views */}
                {isChildView && onClose && (
                    <button
                        onClick={onClose}
                        className={styles.backButton}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clip-path="url(#clip0_35_7)">
                        <path d="M19.0476 8.33333H4.55953L11.2143 1.67857L9.52381 0L0 9.52381L9.52381 19.0476L11.2024 17.369L4.55953 10.7143H19.0476V8.33333Z" fill="#505050"/>
                        </g>
                        <defs>
                        <clipPath id="clip0_35_7">
                        <rect width="20" height="20" fill="white"/>
                        </clipPath>
                        </defs>
                        </svg>
                    </button>
                )}
                <div className={styles.inlineContent}>
                    {activeTab !== 'aplus' && !hideProductImages && (
                        <div className={styles.productImageContainer}>
                            <div className={styles.mainImageWrapper}>
                                {(() => {
                                    const hasAnyImages = allImages.length > 0;
                                    // Use selectedInfographicUrl if set, otherwise use first image
                                    const currentImage = selectedInfographicUrl || (hasAnyImages ? allImages[0] : '');
                                    
                                    return hasAnyImages && currentImage ? (
                                        <img
                                            key={currentImage} // Add key to force re-render when image changes
                                            src={currentImage}
                                            alt="Product Image"
                                            className={styles.mainImage}
                                            onError={(e) => { e.target.src = ''; e.target.alt = 'Image failed to load'; }}
                                        />
                                    ) : (
                                        <div className={styles.imagePlaceholder}>
                                            <span>
                                                {productInput.generate_infographics && !hasImages && !infographicErrors.length > 0
                                                    ? "Generating Infographic..."
                                                    : productInput.generate_infographics && infographicErrors.length > 0
                                                        ? "Infographic Failed"
                                                        : "No Infographics Requested"}
                                            </span>
                                        </div>
                                    );
                                })()}
                                {/* Hide edit button for users with disabled_features.edit_more and bulk results view */}
                                {hasImages && !hideEditMoreOverlay && !authCtx.user?.disabled_features?.edit_more && (
                                    <a
                                        href={`${process.env.REACT_APP_EDITOR_URL}/editor/${editorFolderId}`}
                                        className={styles.editMoreButtonOverlay}
                                    >
                                        <div>
                                            <><svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="37" height="37" rx="18.5" fill="url(#paint0_linear_13_125)"/>
                                            <path d="M25.0981 11.0247C24.7477 10.6879 24.2818 10.5 23.7971 10.5C23.3124 10.5 22.8464 10.6879 22.4961 11.0247L21.1932 12.3333L15.7952 17.7628C15.5602 18.0003 15.3932 18.2972 15.3118 18.6222L14.7751 20.7816C14.7295 20.9632 14.7255 21.1528 14.7636 21.3361C14.8016 21.5194 14.8807 21.6916 14.9947 21.8397C15.1087 21.9877 15.2547 22.1077 15.4217 22.1906C15.5887 22.2734 15.7722 22.317 15.9584 22.3179C16.0601 22.3178 16.1615 22.3052 16.2602 22.2803L18.4071 21.7404C18.7306 21.6588 19.0259 21.4904 19.2616 21.253L24.6595 15.8236L25.9612 14.515C26.3062 14.1679 26.5 13.6972 26.5 13.2064C26.5 12.7156 26.3062 12.2449 25.9612 11.8978L25.0981 11.0247ZM18.3973 20.3806C18.3187 20.4596 18.2204 20.5157 18.1126 20.5429L15.9657 21.0833L16.5031 18.9239C16.5298 18.8154 16.5854 18.7162 16.6638 18.637L21.6287 13.6438L23.364 15.3886L18.3973 20.3806ZM25.0981 13.642L24.2295 14.5144L22.4942 12.7695L23.3622 11.8971C23.479 11.7848 23.6343 11.7221 23.7959 11.7221C23.9574 11.7221 24.1128 11.7848 24.2295 11.8971L25.0981 12.7695C25.2131 12.8852 25.2777 13.0421 25.2777 13.2057C25.2777 13.3694 25.2131 13.5263 25.0981 13.642Z" fill="white"/>
                                            <path d="M13.567 26.5H22.1547C22.9679 26.499 23.7474 26.1737 24.3224 25.5954C24.8974 25.0171 25.2208 24.233 25.2218 23.4151V17.6217C25.2218 17.458 25.1572 17.3011 25.0421 17.1854C24.9271 17.0697 24.7711 17.0047 24.6084 17.0047C24.4457 17.0047 24.2897 17.0697 24.1746 17.1854C24.0596 17.3011 23.995 17.458 23.995 17.6217V23.4151C23.995 23.906 23.8011 24.3768 23.456 24.7239C23.1109 25.071 22.6428 25.266 22.1547 25.266H13.567C13.079 25.266 12.6109 25.071 12.2658 24.7239C11.9207 24.3768 11.7268 23.906 11.7268 23.4151V14.7774C11.7268 14.2865 11.9207 13.8157 12.2658 13.4686C12.6109 13.1215 13.079 12.9265 13.567 12.9265H19.4803C19.643 12.9265 19.799 12.8615 19.914 12.7458C20.0291 12.63 20.0937 12.4731 20.0937 12.3095C20.0937 12.1459 20.0291 11.9889 19.914 11.8732C19.799 11.7575 19.643 11.6925 19.4803 11.6925H13.567C12.7539 11.6935 11.9744 12.0188 11.3994 12.5971C10.8244 13.1754 10.501 13.9595 10.5 14.7774V23.4151C10.501 24.233 10.8244 25.0171 11.3994 25.5954C11.9744 26.1737 12.7539 26.499 13.567 26.5Z" fill="white"/>
                                            <defs>
                                            <linearGradient id="paint0_linear_13_125" x1="-1.37836e-07" y1="18.5" x2="33.9607" y2="37" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                            </linearGradient>
                                            </defs>
                                            </svg>
                                            </>
                                        </div>
                                    </a>
                                )}
                            </div>
                            {allImages.length > 1 && (
                                <div className={styles.thumbnailContainer}>
                                    {allImages.map((url, index) => (
                                        <img
                                            key={`${url}-${index}`}
                                            src={url}
                                            alt={`Thumbnail ${index + 1}`}
                                            className={`${styles.thumbnail} ${selectedInfographicUrl === url ? styles.thumbnailActive : ''}`}
                                            onMouseEnter={(e) => {
                                                e.preventDefault();
                                                handleThumbnailSelect(url);
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleThumbnailSelect(url);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ))}
                                </div>
                            )}
                            {hasInfographicProcessingInfo && !hasImages && infographicErrors.length > 0 && (
                                <div className={styles.errorDisplaySection} style={{marginTop: '15px'}}>
                                   {infographicErrors.map((err, idx) => <div key={idx} className={styles.errorTextPopup}>{err}</div>)}
                                </div>
                            )}
                        </div>
                        
                    )} 
                     {hasInfographicProcessingInfo && !hasImages && infographicErrors.length > 0 && (
                         <div className={styles.errorDisplaySection} style={{marginTop: '15px'}}>
                            {infographicErrors.map((err, idx) => <div key={idx} className={styles.errorTextPopup}>{err}</div>)}
                         </div>
                     )}

                    <div className={`${styles.productDetails} ${activeTab === 'aplus' ? styles.productDetailsFullWidth : ''}`}>
                        <div className={styles.tabs}>
                            <div>
                                <button
                                    className={`${styles.tabButton} ${activeTab === 'content' ? styles.activeTab : ''}`}
                                    onClick={() => setActiveTab('content')}
                                >
                                    Listing Content
                                </button>
                                {hasChildProducts && !isChildView && (
                                    <button
                                        className={`${styles.tabButton} ${activeTab === 'variations' ? styles.activeTab : ''}`}
                                        onClick={() => setActiveTab('variations')}
                                    >
                                        Child Variants ({childProducts.length})
                                    </button>
                                )}
                            </div>
                            {activeTab === 'content' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {hideResultsHeader && isEditMode ? (
                                        <>
                                            <button
                                                className={styles.downloadContentButton}
                                                onClick={cancelEditMode}
                                                disabled={isSaving}
                                            >
                                                <span className={styles.downloadContentButtonText}>Discard Changes</span>
                                            </button>
                                            <button
                                                className={styles.pushToAmazonButton}
                                                onClick={toggleEditMode}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <FaSpinner className={styles.spinner} style={{ marginRight: '8px' }}/>
                                                        <span className={styles.pushToAmazonButtonText}>Saving...</span>
                                                    </>
                                                ) : (
                                                    <span className={styles.pushToAmazonButtonText}>Save Changes</span>
                                                )}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={toggleEditMode}
                                            className={`${styles.editButton} ${styles.btnAmazon} ${styles.btnAmazonYellow}`}
                                            disabled={isSaving}
                                        >
                                            <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M20.3934 0.728785C19.9039 0.26103 19.253 0 18.5759 0C17.8989 0 17.2479 0.26103 16.7584 0.728785L14.9383 2.54629L7.39756 10.0871C7.06923 10.417 6.83596 10.8294 6.72232 11.2807L5.97253 14.2799C5.9088 14.5321 5.9033 14.7954 5.95645 15.05C6.0096 15.3046 6.12 15.5438 6.27928 15.7494C6.43856 15.955 6.64255 16.1217 6.8758 16.2368C7.10904 16.3518 7.36542 16.4123 7.6255 16.4136C7.76764 16.4135 7.90923 16.396 8.0471 16.3614L11.0463 15.6116C11.4982 15.4982 11.9107 15.2642 12.2399 14.9346L19.7807 7.39381L21.5991 5.57631C22.081 5.09423 22.3518 4.44047 22.3518 3.75881C22.3518 3.07715 22.081 2.42339 21.5991 1.94131L20.3934 0.728785ZM11.0326 13.7229C10.9229 13.8327 10.7855 13.9106 10.635 13.9483L7.63578 14.6989L8.38643 11.6998C8.42379 11.5491 8.50139 11.4113 8.61094 11.3013L15.5468 4.36636L17.9709 6.78969L11.0326 13.7229ZM20.3934 4.36379L19.18 5.57545L16.7559 3.15212L17.9684 1.94045C18.1315 1.78447 18.3485 1.69741 18.5742 1.69741C18.7999 1.69741 19.0169 1.78447 19.18 1.94045L20.3934 3.15212C20.5541 3.31281 20.6443 3.53073 20.6443 3.75795C20.6443 3.98517 20.5541 4.20309 20.3934 4.36379Z" fill="#0752B0"/>
                                            <path d="M4.28454 22.2221H16.2812C17.4172 22.2207 18.5062 21.7689 19.3094 20.9656C20.1126 20.1624 20.5644 19.0734 20.5658 17.9375V9.89116C20.5658 9.6639 20.4755 9.44594 20.3148 9.28524C20.1541 9.12454 19.9361 9.03425 19.7089 9.03425C19.4816 9.03425 19.2636 9.12454 19.1029 9.28524C18.9422 9.44594 18.852 9.6639 18.852 9.89116V17.9375C18.852 18.6193 18.5811 19.2732 18.099 19.7553C17.6169 20.2374 16.963 20.5082 16.2812 20.5082H4.28454C3.60274 20.5082 2.94887 20.2374 2.46676 19.7553C1.98466 19.2732 1.71382 18.6193 1.71382 17.9375V5.94082C1.71382 5.25902 1.98466 4.60515 2.46676 4.12304C2.94887 3.64094 3.60274 3.3701 4.28454 3.3701H12.5451C12.7724 3.3701 12.9904 3.27981 13.1511 3.11911C13.3118 2.95841 13.402 2.74045 13.402 2.51319C13.402 2.28592 13.3118 2.06796 13.1511 1.90726C12.9904 1.74656 12.7724 1.65628 12.5451 1.65628H4.28454C3.14863 1.65764 2.05963 2.10948 1.25641 2.9127C0.453203 3.71591 0.00136065 4.80491 0 5.94082V17.9375C0.00136065 19.0734 0.453203 20.1624 1.25641 20.9656C2.05963 21.7689 3.14863 22.2207 4.28454 22.2221Z" fill="#0752B0"/>
                                            </svg>
                                        </button>
                                    )}
                                    {hideResultsHeader && !hideDownloadButton && (
                                        <button
                                            className={styles.downloadContentIconButton}
                                            onClick={handleDownload}
                                            disabled={isDownloading}
                                            title="Download Content"
                                        >
                                            <DownloadCircleIcon />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={styles.detailsScrollableContent}>
                            {allErrorsAndWarnings.length > 0 && activeTab === 'content' && (
                                <div className={`${styles.productSection} ${styles.errorDisplaySection}`}>
                                    {allErrorsAndWarnings.map((item, idx) => (
                                        <div key={idx} className={item.type === 'error' ? styles.errorTextPopup : item.type === 'warning' ? styles.warningTextPopup : styles.infoTextPopup}>
                                            <strong>{item.text}</strong>
                                            {item.items && item.items.length > 0 && (
                                                <ul>{item.items.map((subItem, subIdx) => <li key={subIdx}>{subItem}</li>)}</ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {hasBannedWords && activeTab === 'content' && (
                                <div className={`${styles.productSection} ${styles.errorDisplaySection}`}>
                                    <div className={styles.warningTextPopup}>
                                        <strong>Banned Keywords Detected</strong>
                                        <p>Some words in your content are highlighted in red as they may violate Amazon's policies. Please review and edit these words before listing.</p>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'content' && (
                                <>
                                    <div className={styles.productSection}>
                                        <div className={styles.sectionHeader}>
                                            <h2 className={styles.sectionHeading}>
                                                Title
                                                <button className={styles.redoButton} type="button" title="Regenerate title with instructions" onClick={() => openRegen('title')}>
                                                    <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M8.98474 12.9039C8.98474 13.1564 8.77866 13.3712 8.51735 13.3712C8.25514 13.3712 8.04996 13.1564 8.04996 12.9039C8.04996 10.7431 6.29175 8.98492 4.13102 8.98492C3.87847 8.98492 3.66364 8.77885 3.66364 8.51753C3.66364 8.25533 3.87848 8.05014 4.13102 8.05014C6.29175 8.05014 8.04996 6.29193 8.04996 4.13121C8.04996 3.87866 8.25603 3.66382 8.51735 3.66382C8.77955 3.66382 8.98474 3.87866 8.98474 4.13121C8.98474 6.29193 10.7429 8.05014 12.9037 8.05014C13.1562 8.05014 13.3711 8.25622 13.3711 8.51753C13.3711 8.77974 13.1562 8.98492 12.9037 8.98492C10.7438 8.98405 8.98474 10.7431 8.98474 12.9039Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                    <path d="M1.08038 2.87607C0.846239 2.90413 0.603341 2.69805 0.584932 2.43674C0.566517 2.17453 0.762947 1.95969 1.01551 1.94129L3.55943 1.76327H3.59714C3.71903 1.76327 3.84004 1.80975 3.92422 1.90358C4.01805 1.99741 4.07418 2.12807 4.06453 2.26838L3.88652 4.8123C3.8681 5.07451 3.62431 5.27094 3.39106 5.24288C3.12886 5.22446 2.94208 4.99998 2.96049 4.74743L3.05432 3.47501C1.78191 4.85967 1.07162 6.63629 1.07162 8.52605C1.07162 10.5184 1.84769 12.3985 3.2604 13.8016C5.2247 15.7659 8.08695 16.4771 10.7431 15.6536C10.986 15.5791 11.2482 15.7194 11.3228 15.9623C11.3973 16.2052 11.257 16.4674 11.0141 16.5419C8.02118 17.468 4.79412 16.6638 2.59569 14.4557C0.986539 12.8659 0.116638 10.7613 0.116638 8.5164C0.116638 6.36533 0.930417 4.34491 2.38961 2.78224L1.08038 2.87607Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                    <path d="M16.9172 8.51663C16.9172 10.6677 16.1035 12.6881 14.6443 14.2508L15.9535 14.157C16.1964 14.1289 16.4306 14.335 16.449 14.5875C16.4674 14.8497 16.2709 15.0646 16.0184 15.083L13.4745 15.2698C13.3438 15.2794 13.2035 15.2321 13.1097 15.1295C13.0158 15.0356 12.9597 14.905 12.9694 14.7647L13.1474 12.2207C13.1658 11.9778 13.3719 11.7902 13.6148 11.7902H13.6525C13.9147 11.8086 14.1014 12.0331 14.083 12.2856L13.9892 13.558C15.2616 12.1734 15.9719 10.3968 15.9719 8.50699C15.9719 6.51464 15.1958 4.63453 13.7831 3.23146C11.8188 1.26717 8.95658 0.555981 6.3004 1.37941C6.0575 1.4636 5.7953 1.31364 5.72077 1.07074C5.64624 0.827832 5.78654 0.565633 6.02944 0.491102C6.85287 0.23855 7.68506 0.116652 8.51725 0.116652C10.7157 0.116652 12.8483 0.9769 14.4478 2.5764C16.0473 4.16711 16.9172 6.27172 16.9172 8.51663Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                    </svg>
                                                </button>
                                            </h2>
                                        </div>
                                        {isEditMode ? (
                                            <div className={styles.editorContainer}>
                                                <Editor
                                                    editorState={titleEditorState}
                                                    onChange={setTitleEditorState}
                                                    placeholder="Enter product title"
                                                />
                                                {(() => {
                                                    const len = titleEditorState.getCurrentContent().getPlainText().length;
                                                    return (
                                                        <span className={styles.charCount} style={len > TITLE_MAX_LENGTH ? { color: '#c62828' } : undefined}>
                                                            {len}/{TITLE_MAX_LENGTH} characters{len > TITLE_MAX_LENGTH ? ` — Title exceeds ${TITLE_MAX_LENGTH} characters` : ''}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            <>
                                                <div
                                                    className={styles.productTitle}
                                                    dangerouslySetInnerHTML={{
                                                        __html: highlightBannedWords(finalTitle)
                                                    }}
                                                />
                                                <div className={styles.countRow}>
                                                    {isLegacyFormat ? (
                                                        <span className={styles.charCount}>{finalTitle.length} characters</span>
                                                    ) : (
                                                        <span className={styles.charCount} style={finalTitle.length > TITLE_MAX_LENGTH ? { color: '#c62828' } : undefined}>
                                                            {finalTitle.length}/{TITLE_MAX_LENGTH} characters
                                                        </span>
                                                    )}
                                                    <KeywordCount keywords={titleKeywords} />
                                                </div>
                                            </>
                                        )}

                                        {isLegacyFormat && (
                                            <div className={styles.formatNotice}>
                                                <span className={styles.formatNoticeIcon} aria-hidden="true">i</span>
                                                <span className={styles.formatNoticeText}>
                                                    <span className={styles.formatNoticeLead}>Old title format</span>
                                                    <span className={styles.formatNoticeBody}>{LEGACY_FORMAT_MESSAGE}</span>
                                                </span>
                                                <button
                                                    type="button"
                                                    className={styles.formatNoticeButton}
                                                    onClick={convertTitleFormat}
                                                    disabled={titleFormat.loading || !listing_id}
                                                    title={!listing_id ? 'Save this listing before updating its format' : ''}
                                                >
                                                    {titleFormat.loading ? 'Updating…' : 'Update to new format'}
                                                </button>
                                            </div>
                                        )}
                                        {isLegacyFormat && titleFormat.error && (
                                            <span className={styles.formatNoticeError}>{titleFormat.error}</span>
                                        )}
                                    </div>

                                    {renderRegenBox('title')}

                                    {/* Hidden for pre-31-Jul-2026 listings: the field did not exist then. */}
                                    {!isLegacyFormat && (
                                    <div className={styles.productSection}>
                                        <div className={styles.sectionHeader}>
                                            <h2 className={styles.sectionHeading}>
                                                Item Highlights
                                                <button className={styles.redoButton} type="button" title="Regenerate item highlights with instructions" onClick={() => openRegen('item_highlights')}>
                                                    <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M8.98474 12.9039C8.98474 13.1564 8.77866 13.3712 8.51735 13.3712C8.25514 13.3712 8.04996 13.1564 8.04996 12.9039C8.04996 10.7431 6.29175 8.98492 4.13102 8.98492C3.87847 8.98492 3.66364 8.77885 3.66364 8.51753C3.66364 8.25533 3.87848 8.05014 4.13102 8.05014C6.29175 8.05014 8.04996 6.29193 8.04996 4.13121C8.04996 3.87866 8.25603 3.66382 8.51735 3.66382C8.77955 3.66382 8.98474 3.87866 8.98474 4.13121C8.98474 6.29193 10.7429 8.05014 12.9037 8.05014C13.1562 8.05014 13.3711 8.25622 13.3711 8.51753C13.3711 8.77974 13.1562 8.98492 12.9037 8.98492C10.7438 8.98405 8.98474 10.7431 8.98474 12.9039Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                    <path d="M1.08038 2.87607C0.846239 2.90413 0.603341 2.69805 0.584932 2.43674C0.566517 2.17453 0.762947 1.95969 1.01551 1.94129L3.55943 1.76327H3.59714C3.71903 1.76327 3.84004 1.80975 3.92422 1.90358C4.01805 1.99741 4.07418 2.12807 4.06453 2.26838L3.88652 4.8123C3.8681 5.07451 3.62431 5.27094 3.39106 5.24288C3.12886 5.22446 2.94208 4.99998 2.96049 4.74743L3.05432 3.47501C1.78191 4.85967 1.07162 6.63629 1.07162 8.52605C1.07162 10.5184 1.84769 12.3985 3.2604 13.8016C5.2247 15.7659 8.08695 16.4771 10.7431 15.6536C10.986 15.5791 11.2482 15.7194 11.3228 15.9623C11.3973 16.2052 11.257 16.4674 11.0141 16.5419C8.02118 17.468 4.79412 16.6638 2.59569 14.4557C0.986539 12.8659 0.116638 10.7613 0.116638 8.5164C0.116638 6.36533 0.930417 4.34491 2.38961 2.78224L1.08038 2.87607Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                    <path d="M16.9172 8.51663C16.9172 10.6677 16.1035 12.6881 14.6443 14.2508L15.9535 14.157C16.1964 14.1289 16.4306 14.335 16.449 14.5875C16.4674 14.8497 16.2709 15.0646 16.0184 15.083L13.4745 15.2698C13.3438 15.2794 13.2035 15.2321 13.1097 15.1295C13.0158 15.0356 12.9597 14.905 12.9694 14.7647L13.1474 12.2207C13.1658 11.9778 13.3719 11.7902 13.6148 11.7902H13.6525C13.9147 11.8086 14.1014 12.0331 14.083 12.2856L13.9892 13.558C15.2616 12.1734 15.9719 10.3968 15.9719 8.50699C15.9719 6.51464 15.1958 4.63453 13.7831 3.23146C11.8188 1.26717 8.95658 0.555981 6.3004 1.37941C6.0575 1.4636 5.7953 1.31364 5.72077 1.07074C5.64624 0.827832 5.78654 0.565633 6.02944 0.491102C6.85287 0.23855 7.68506 0.116652 8.51725 0.116652C10.7157 0.116652 12.8483 0.9769 14.4478 2.5764C16.0473 4.16711 16.9172 6.27172 16.9172 8.51663Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                    </svg>
                                                </button>
                                            </h2>
                                        </div>
                                        {isEditMode ? (
                                            <div className={styles.editorContainer}>
                                                <Editor
                                                    editorState={itemHighlightsEditorState}
                                                    onChange={setItemHighlightsEditorState}
                                                    placeholder="Enter item highlights (searchable, up to 125 characters)"
                                                />
                                                {(() => {
                                                    const len = itemHighlightsEditorState.getCurrentContent().getPlainText().length;
                                                    return (
                                                        <span className={styles.charCount} style={len > ITEM_HIGHLIGHTS_MAX_LENGTH ? { color: '#c62828' } : undefined}>
                                                            {len}/{ITEM_HIGHLIGHTS_MAX_LENGTH} characters{len > ITEM_HIGHLIGHTS_MAX_LENGTH ? ` — Item highlights exceed ${ITEM_HIGHLIGHTS_MAX_LENGTH} characters` : ''}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            <>
                                                {finalItemHighlights ? (
                                                    <div className={styles.productTitle}>{finalItemHighlights}</div>
                                                ) : (
                                                    <span className={styles.charCount}>No item highlights generated yet — regenerate to add them.</span>
                                                )}
                                                <div className={styles.countRow}>
                                                    <span className={styles.charCount} style={finalItemHighlights.length > ITEM_HIGHLIGHTS_MAX_LENGTH ? { color: '#c62828' } : undefined}>
                                                        {finalItemHighlights.length}/{ITEM_HIGHLIGHTS_MAX_LENGTH} characters
                                                    </span>
                                                    <KeywordCount keywords={itemHighlightsKeywords} />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    )}

                                    {!isLegacyFormat && renderRegenBox('item_highlights')}

                                    {(bullets.length > 0 || isEditMode) && (
                                        <div className={`${styles.productSection} ${styles.bulletPointsContainer}`}>
                                            <div className={styles.sectionHeader}>
                                                <h2 className={styles.sectionHeading}>
                                                   Bullet Points
                                                    <button className={styles.redoButton} type="button" title="Regenerate bullet points with instructions" onClick={() => openRegen('bullets')}>
                                                        <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8.98474 12.9039C8.98474 13.1564 8.77866 13.3712 8.51735 13.3712C8.25514 13.3712 8.04996 13.1564 8.04996 12.9039C8.04996 10.7431 6.29175 8.98492 4.13102 8.98492C3.87847 8.98492 3.66364 8.77885 3.66364 8.51753C3.66364 8.25533 3.87848 8.05014 4.13102 8.05014C6.29175 8.05014 8.04996 6.29193 8.04996 4.13121C8.04996 3.87866 8.25603 3.66382 8.51735 3.66382C8.77955 3.66382 8.98474 3.87866 8.98474 4.13121C8.98474 6.29193 10.7429 8.05014 12.9037 8.05014C13.1562 8.05014 13.3711 8.25622 13.3711 8.51753C13.3711 8.77974 13.1562 8.98492 12.9037 8.98492C10.7438 8.98405 8.98474 10.7431 8.98474 12.9039Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                        <path d="M1.08038 2.87607C0.846239 2.90413 0.603341 2.69805 0.584932 2.43674C0.566517 2.17453 0.762947 1.95969 1.01551 1.94129L3.55943 1.76327H3.59714C3.71903 1.76327 3.84004 1.80975 3.92422 1.90358C4.01805 1.99741 4.07418 2.12807 4.06453 2.26838L3.88652 4.8123C3.8681 5.07451 3.62431 5.27094 3.39106 5.24288C3.12886 5.22446 2.94208 4.99998 2.96049 4.74743L3.05432 3.47501C1.78191 4.85967 1.07162 6.63629 1.07162 8.52605C1.07162 10.5184 1.84769 12.3985 3.2604 13.8016C5.2247 15.7659 8.08695 16.4771 10.7431 15.6536C10.986 15.5791 11.2482 15.7194 11.3228 15.9623C11.3973 16.2052 11.257 16.4674 11.0141 16.5419C8.02118 17.468 4.79412 16.6638 2.59569 14.4557C0.986539 12.8659 0.116638 10.7613 0.116638 8.5164C0.116638 6.36533 0.930417 4.34491 2.38961 2.78224L1.08038 2.87607Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                        <path d="M16.9172 8.51663C16.9172 10.6677 16.1035 12.6881 14.6443 14.2508L15.9535 14.157C16.1964 14.1289 16.4306 14.335 16.449 14.5875C16.4674 14.8497 16.2709 15.0646 16.0184 15.083L13.4745 15.2698C13.3438 15.2794 13.2035 15.2321 13.1097 15.1295C13.0158 15.0356 12.9597 14.905 12.9694 14.7647L13.1474 12.2207C13.1658 11.9778 13.3719 11.7902 13.6148 11.7902H13.6525C13.9147 11.8086 14.1014 12.0331 14.083 12.2856L13.9892 13.558C15.2616 12.1734 15.9719 10.3968 15.9719 8.50699C15.9719 6.51464 15.1958 4.63453 13.7831 3.23146C11.8188 1.26717 8.95658 0.555981 6.3004 1.37941C6.0575 1.4636 5.7953 1.31364 5.72077 1.07074C5.64624 0.827832 5.78654 0.565633 6.02944 0.491102C6.85287 0.23855 7.68506 0.116652 8.51725 0.116652C10.7157 0.116652 12.8483 0.9769 14.4478 2.5764C16.0473 4.16711 16.9172 6.27172 16.9172 8.51663Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                        </svg>
                                                    </button>
                                                </h2>
                                            </div>
                                            {isEditMode ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                    {bulletEditorStates.map((editorState, index) => {
                                                        const len = editorState.getCurrentContent().getPlainText().length;
                                                        return (
                                                            <div key={index} className={styles.editorContainer}>
                                                                <Editor
                                                                    editorState={editorState}
                                                                    onChange={(newState) => {
                                                                        const newStates = [...bulletEditorStates];
                                                                        newStates[index] = newState;
                                                                        setBulletEditorStates(newStates);
                                                                    }}
                                                                    placeholder={`Bullet point ${index + 1}`}
                                                                />
                                                                <span className={styles.charCount} style={len > 300 ? { color: '#c62828' } : undefined}>
                                                                    {len}/300 characters{len > 300 ? ' — Exceeds 300 characters' : ''}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <>
                                                    <ul className={styles.bulletPoints}>
                                                        {finalBullets.map((point, index) => (
                                                            <li key={index} className={styles.bulletWithCount}>
                                                                <span>{point}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    {finalBullets.length > 0 && (
                                                        <div className={styles.countRow}>
                                                            <span className={styles.charCount}>
                                                                {Math.round(finalBullets.reduce((sum, bullet) => sum + bullet.length, 0) / finalBullets.length)} Characters Average
                                                            </span>
                                                            <KeywordCount keywords={bulletKeywords} />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {renderRegenBox('bullets')}
                                </>
                            )}

                            {activeTab === 'callouts' && (
                                <div className={styles.productSection}>
                                    <h2 className={styles.bulletPointsHeading} title={!hasCallouts ? 'Callouts not available yet' : ''}>Key Feature Callouts</h2>
                                    {hasCallouts ? (
                                        <>
                                            <ul className={styles.calloutList}>
                                                {callouts.map((callout, index) => (
                                                    <li key={index}>
                                                        <span>{callout}</span>
                                                        <button
                                                            onClick={() => copyToClipboard(callout, `Callout ${index + 1}`)}
                                                            title={`Copy Callout ${index + 1}`}
                                                            className={styles.copyInlineButton}
                                                        >
                                                            {copiedStates[`Callout ${index + 1}`] ? <FaCheck /> : <FaCopy />}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                            <button onClick={copyAllCallouts} className={styles.copyAllButton}>
                                                {copiedStates['All Callouts'] ? <FaCheck /> : <FaCopy />} Copy All Callouts
                                            </button>
                                        </>
                                    ) : (
                                        <p>No callouts were generated.</p>
                                    )}
                                </div>
                            )}

                            {activeTab === 'variations' && hasChildProducts && (
                                <div className={styles.variationsContainer}>
                                    {childProducts.map((child, index) => {
                                        const { bplus } = getChildInfographics(child, child.child_index || index, childInfographics);
                                        const singleImageTemplate = bplus?.find(t => t.template_id === 'single_image');
                                        const childImageUrl = extractImageUrl(singleImageTemplate?.images?.[0]);
                                        const childSku = child.model_number || child.sku || 'N/A';
                                        const childTitle = child.title || 'No title';
                                        
                                        return (
                                            <React.Fragment key={child.model_number || child.sku || index}>
                                                {index > 0 && <div className={styles.variationDivider} />}
                                                <div 
                                                    className={styles.variationItem}
                                                    onClick={() => openChildDetails(child, child.child_index || index)}
                                                >
                                                    <div className={styles.variationImageContainer}>
                                                        {childImageUrl ? (
                                                            <img 
                                                                src={childImageUrl} 
                                                                alt={childTitle}
                                                                className={styles.variationImage}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className={styles.variationImagePlaceholder} />
                                                        )}
                                                    </div>
                                                    <div className={styles.variationContent}>
                                                        <div className={styles.variationContentInner}>
                                                            <div className={styles.variationTitle}>{childTitle}</div>
                                                            <div className={styles.variationSkuBadge}>
                                                                <span className={styles.variationSkuText}>SKU ID : {childSku}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div> 
                
                {(description || isEditMode) && (
                    <div className={`${styles.productSection} ${styles.descriptionContainer}`}>
                                            <div className={styles.sectionHeader}>
                                                <h2 className={styles.sectionHeading}>
                                                    Product Description
                                                    <button className={styles.redoButton} type="button" title="Regenerate description with instructions" onClick={() => openRegen('description')}>
                                                        <svg width="22" height="22" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8.98474 12.9039C8.98474 13.1564 8.77866 13.3712 8.51735 13.3712C8.25514 13.3712 8.04996 13.1564 8.04996 12.9039C8.04996 10.7431 6.29175 8.98492 4.13102 8.98492C3.87847 8.98492 3.66364 8.77885 3.66364 8.51753C3.66364 8.25533 3.87848 8.05014 4.13102 8.05014C6.29175 8.05014 8.04996 6.29193 8.04996 4.13121C8.04996 3.87866 8.25603 3.66382 8.51735 3.66382C8.77955 3.66382 8.98474 3.87866 8.98474 4.13121C8.98474 6.29193 10.7429 8.05014 12.9037 8.05014C13.1562 8.05014 13.3711 8.25622 13.3711 8.51753C13.3711 8.77974 13.1562 8.98492 12.9037 8.98492C10.7438 8.98405 8.98474 10.7431 8.98474 12.9039Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                        <path d="M1.08038 2.87607C0.846239 2.90413 0.603341 2.69805 0.584932 2.43674C0.566517 2.17453 0.762947 1.95969 1.01551 1.94129L3.55943 1.76327H3.59714C3.71903 1.76327 3.84004 1.80975 3.92422 1.90358C4.01805 1.99741 4.07418 2.12807 4.06453 2.26838L3.88652 4.8123C3.8681 5.07451 3.62431 5.27094 3.39106 5.24288C3.12886 5.22446 2.94208 4.99998 2.96049 4.74743L3.05432 3.47501C1.78191 4.85967 1.07162 6.63629 1.07162 8.52605C1.07162 10.5184 1.84769 12.3985 3.2604 13.8016C5.2247 15.7659 8.08695 16.4771 10.7431 15.6536C10.986 15.5791 11.2482 15.7194 11.3228 15.9623C11.3973 16.2052 11.257 16.4674 11.0141 16.5419C8.02118 17.468 4.79412 16.6638 2.59569 14.4557C0.986539 12.8659 0.116638 10.7613 0.116638 8.5164C0.116638 6.36533 0.930417 4.34491 2.38961 2.78224L1.08038 2.87607Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                        <path d="M16.9172 8.51663C16.9172 10.6677 16.1035 12.6881 14.6443 14.2508L15.9535 14.157C16.1964 14.1289 16.4306 14.335 16.449 14.5875C16.4674 14.8497 16.2709 15.0646 16.0184 15.083L13.4745 15.2698C13.3438 15.2794 13.2035 15.2321 13.1097 15.1295C13.0158 15.0356 12.9597 14.905 12.9694 14.7647L13.1474 12.2207C13.1658 11.9778 13.3719 11.7902 13.6148 11.7902H13.6525C13.9147 11.8086 14.1014 12.0331 14.083 12.2856L13.9892 13.558C15.2616 12.1734 15.9719 10.3968 15.9719 8.50699C15.9719 6.51464 15.1958 4.63453 13.7831 3.23146C11.8188 1.26717 8.95658 0.555981 6.3004 1.37941C6.0575 1.4636 5.7953 1.31364 5.72077 1.07074C5.64624 0.827832 5.78654 0.565633 6.02944 0.491102C6.85287 0.23855 7.68506 0.116652 8.51725 0.116652C10.7157 0.116652 12.8483 0.9769 14.4478 2.5764C16.0473 4.16711 16.9172 6.27172 16.9172 8.51663Z" fill="#78B3F3" stroke="#78B3F3" strokeWidth="0.233333"/>
                                                        </svg>
                                                    </button>
                                                </h2>
                                            </div>
                                            {isEditMode ? (
                                                <div className={styles.editorContainer}>
                                                    <Editor
                                                        editorState={descriptionEditorState}
                                                        onChange={setDescriptionEditorState}
                                                        placeholder="Enter product description"
                                                    />
                                                    {(() => {
                                                        const len = descriptionEditorState.getCurrentContent().getPlainText().length;
                                                        return (
                                                            <span className={styles.charCount} style={len > 1200 ? { color: '#c62828' } : undefined}>
                                                                {len}/1200 characters{len > 1200 ? ' — Description exceeds 1200 characters' : ''}
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.productDescription}>
                                                        {displayedDescription}
                                                    </div>
                                                    {needsTruncation && (
                                                        <button 
                                                            onClick={toggleReadMore} 
                                                            className={`${styles.readMoreButton} ${isExpanded ? styles.expanded : ''}`}
                                                        >
                                                            {isExpanded ? 'Read Less' : 'Read More'}
                                                        </button>
                                                    )}
                                                    <div className={styles.countRow}>
                                                        <span className={styles.charCount}>
                                                            {finalDescription.length} characters
                                                        </span>
                                                        <KeywordCount keywords={descriptionKeywords} />
                                                    </div>
                                                </>
                                            )}
                    </div>
                                    )}

                {renderRegenBox('description')}

                {hasAplusTemplates && (
                    <div className={styles.aplusCarouselContainer}>
                        {aplusTemplates.flatMap((template, templateIndex) => {
                            // Handle both structures: template.images array or template.image_url.url
                            let images = [];
                            if (template.images && Array.isArray(template.images)) {
                                images = template.images;
                            } else if (template.image_url?.url) {
                                images = [template.image_url.url];
                            }
                            
                            return images.map((imageUrl, imageIndex) => {
                                const url = extractImageUrl(imageUrl);
                                if (!url) return null;
                                return (
                                    <div key={`${templateIndex}-${imageIndex}`} className={styles.aplusImageContainer}>
                                        <img
                                            src={url}
                                            alt={`A+ Template ${templateIndex + 1} - Image ${imageIndex + 1}`}
                                            className={styles.aplusMainImage}
                                            onError={(e) => { 
                                                e.target.src = ''; 
                                                e.target.alt = 'A+ template failed to load'; 
                                            }}
                                        />
                                    </div>
                                );
                            });
                        }).filter(Boolean)}
                    </div>
                )}


            </div>
            
            {/* Amazon Validation Modal */}
            <AmazonValidationModal
                show={showTitleLengthModal}
                onClose={() => setShowTitleLengthModal(false)}
                title={
                    validationError.type === 'banned_words' ? 'Title Contains Banned Keywords' :
                    validationError.type === 'title_length' ? `Title Exceeds ${TITLE_MAX_LENGTH} Characters` :
                    validationError.type === 'both' ? 'Title Validation Error' :
                    validationError.type === 'legacy_format' ? 'Listing Uses the Old Title Format' :
                    'Validation Error'
                }
                message={validationError.message}
            />

            {/* Amazon Image Selection Modal */}
            <AmazonImageSelectionModal
                show={showImageSelectionModal}
                onClose={() => setShowImageSelectionModal(false)}
                maxGallery={9}
                loadingExisting={isFetchingExistingImages}
                galleryImages={[
                    ...allImages.map(url => ({ url, source: 'generated' })),
                    ...existingAmazonImages
                        .filter(url => !allImages.includes(url))
                        .map(url => ({ url, source: 'existing' })),
                ]}
                aplusImages={(() => {
                    const generated = (hasAplusTemplates ? aplusTemplates : [])
                        .map(t => t.image_url?.url)
                        .filter(Boolean);
                    const generatedSet = new Set(generated);
                    return [
                        ...generated.map(url => ({ url, source: 'generated' })),
                        ...existingAplusImages
                            .filter(url => !generatedSet.has(url))
                            .map(url => ({ url, source: 'existing' })),
                    ];
                })()}
                onConfirm={handleImageSelectionConfirm}
            />

            {/* Amazon Processing Time Modal */}
            <AmazonProcessingTimeModal
                show={showProcessingTimeModal}
                onClose={() => setShowProcessingTimeModal(false)}
                onConfirm={handleConfirmProcessingTime}
            />

        </>
        );
    }
export default SingleResultsCard;